// ** Type Imports
import type { UserDTO } from '@/entities/user'

// ** Environment variables
import { env } from './env'

// ** Helper Functions
import { getFullName } from './helpers'

interface TawkApi {
	visitor?: {
		userId?: string
		name?: string
		email?: string
		hash?: string
	}

	autoStart?: boolean

	start?: (data: Record<string, any>) => void
	onLoad?: () => void
	onBeforeLoad?: () => void

	setAttributes?: (
		attributes: Record<string, string | number>,
		callback?: (error?: unknown) => void
	) => void

	login?: (
		payload: Record<string, string | number>,
		callback?: (error?: unknown) => void
	) => void

	// Ends the current visitor session so the next user starts fresh. Available
	// regardless of secure mode.
	logout?: (callback?: (error?: unknown) => void) => void
	endChat?: () => void
	hideWidget?: () => void
	showWidget?: () => void
}

declare global {
	interface Window {
		Tawk_API?: TawkApi
		Tawk_LoadStart?: Date
	}
}

const TAWK_SCRIPT_ID = 'tawk-to-embed'

// The user we have already set attributes for. Kept at module scope so repeated
// renders / store updates cannot re-run setAttributes for the same user.
let currentAttributeUserId: string | null = null

let isLoggedIn: boolean = false

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applyAttributes = (attributes: Record<string, string | number>) => {
	window.Tawk_API?.setAttributes?.(attributes, (error) => {
		if (error) {
			console.error('[Tawk] setAttributes failed:', JSON.stringify(error))
		}
	})
}

// Expire every Tawk cookie (twk_idm_key, twk_uuid_<propertyId>, twk_token_*, ...)
// that was set on the parent domain, so the next user starts a fresh visitor.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearTawkCookies = () => {
	if (typeof document === 'undefined' || !document.cookie) return

	const { hostname } = window.location
	const expiry = 'Thu, 01 Jan 1970 00:00:00 GMT'

	for (const cookie of document.cookie.split(';')) {
		const name = cookie.split('=')[0]?.trim()
		if (!name || (!name.startsWith('twk_') && name !== 'TawkConnectionTime')) continue

		// Clear across the path/domain combinations Tawk may have used.
		document.cookie = `${name}=; expires=${expiry}; path=/`
		document.cookie = `${name}=; expires=${expiry}; path=/; domain=${hostname}`
		document.cookie = `${name}=; expires=${expiry}; path=/; domain=.${hostname}`
	}
}

// Remove all Tawk localStorage entries (twk_*, twk_token_*).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearTawkStorage = () => {
	try {
		const keysToRemove: string[] = []

		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i)
			if (key && key.startsWith('twk_')) {
				keysToRemove.push(key)
			}
		}

		keysToRemove.forEach((key) => {
			window.localStorage.removeItem(key)
		})
	} catch (error) {
		console.error('[Tawk] failed to clear localStorage:', JSON.stringify(error))
	}
}

export const loadTawkTo = (user: UserDTO | null) => {
	const { tawkPropertyId, tawkWidgetId } = env
	const getHash = localStorage.getItem('tawk_custom_token_hash')

	if (!tawkPropertyId || !tawkWidgetId || !user) return

	const userId = String(user.id)
	const orgId = String(user.organizationId)

	// Attributes already set for this user - nothing to do.
	if (currentAttributeUserId === userId) return
	currentAttributeUserId = userId

	const api: TawkApi = window.Tawk_API ?? {}

	window.Tawk_API = api
	window.Tawk_LoadStart ??= new Date()
	api.autoStart = false

	// const attributes: Record<string, string | number> = {
	// 	name: getFullName(user.firstName, user.lastName),
	// 	email: user.email,
	// 	hash: user.tawkToUserHash,
	// 	userId,
	// 	orgId,
	// }

	// api.onLoad = () => {
	// 	applyAttributes(attributes)
	// }

	const payload: Record<string, string | number> = {
		name: getFullName(user.firstName, user.lastName),
		email: user.email,
		hash: user.tawkToUserHash,
		userId: user.email,
		orgId
	}

	api.onBeforeLoad = () => {
		if (!isLoggedIn && !getHash) {
			console.log('[Tawk][debug] logging in for', payload.userId)
			window.Tawk_API?.login?.(payload, (error) => {
				if (error) {
					console.error('[Tawk] login failed:', JSON.stringify(error))
				} else {
					console.log('[Tawk][debug] login success for', payload.userId)
					isLoggedIn = true
					api?.start?.({ showWidget : true })
					localStorage.setItem('tawk_custom_token_hash', `${payload.hash}`)
				}
			})
		} else {
			console.log('[Tawk][debug] skipping login for', payload.userId);
			
			isLoggedIn = true
			api?.start?.({ showWidget : true })
		}
	}

	const script = document.createElement('script')
	script.id = TAWK_SCRIPT_ID
	script.async = true
	script.src = `https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}`
	script.charset = 'UTF-8'
	script.setAttribute('crossorigin', '*')
	document.body.appendChild(script)
}

// Call on logout. Ends the Tawk visitor session (so the next user who logs in
// on this browser does NOT inherit the previous conversation) and hides the
// widget so it is no longer visible.
export const shutdownTawkTo = () => {
	currentAttributeUserId = null

	const api = window.Tawk_API
	if (!api) return

	try {
		// api.endChat?.()

		// Hide immediately, then again after logout completes (logout can spin up
		// a new anonymous session that re-shows the widget).
		api.hideWidget?.()

		api.logout?.(() => {
			isLoggedIn = false
			localStorage.removeItem('tawk_custom_token_hash')
			window.Tawk_API?.hideWidget?.()
		})

		// Remove twk_idm_key, twk_uuid_* from cookies
		// clearTawkCookies()

		// Remove twk_*, twk_token_* from localStorage
		// clearTawkStorage()

		window.Tawk_API = {}

		document.getElementById(TAWK_SCRIPT_ID)?.remove()

		window.location.pathname = '/login'
		window.location.reload()
	} catch (error) {
		console.error('[Tawk] shutdown failed:', JSON.stringify(error))
	}
}
